import { useState, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useChartOfAccounts, type ChartOfAccount } from "@/hooks/useChartOfAccounts";

const TYPE_LABELS: Record<string, string> = {
  income: "Income",
  cogs: "Cost of Goods Sold",
  expense: "Expense",
  liability: "Liability",
  asset: "Asset",
  equity: "Equity",
};

interface Props {
  value: string | null;
  onValueChange: (id: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function AccountSelect({ value, onValueChange, placeholder = "Select account…", className, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const { data: accounts, isLoading } = useChartOfAccounts();

  const grouped = useMemo(() => {
    const groups: Record<string, ChartOfAccount[]> = {};
    for (const a of accounts ?? []) {
      const type = a.account_type || "other";
      if (!groups[type]) groups[type] = [];
      groups[type].push(a);
    }
    return groups;
  }, [accounts]);

  const selected = useMemo(
    () => (accounts ?? []).find((a) => a.id === value),
    [accounts, value]
  );

  const displayLabel = selected
    ? `${selected.account_number ? selected.account_number + " — " : ""}${selected.name}`
    : placeholder;

  if (!accounts?.length && !isLoading) {
    return (
      <Button variant="outline" className={cn("w-full justify-start text-muted-foreground font-normal", className)} disabled>
        No accounts configured
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", !value && "text-muted-foreground", className)}
          disabled={disabled || isLoading}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search accounts…" />
          <CommandList className="max-h-[300px] overflow-y-auto overscroll-contain" style={{ pointerEvents: "auto" }} onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>No account found.</CommandEmpty>
            {Object.entries(grouped).map(([type, accts]) => (
              <CommandGroup key={type} heading={TYPE_LABELS[type] ?? type}>
                {accts.map((a) => (
                  <CommandItem
                    key={a.id}
                    value={`${a.account_number} ${a.name}`}
                    onSelect={() => {
                      onValueChange(a.id === value ? null : a.id);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === a.id ? "opacity-100" : "opacity-0")} />
                    <span className="text-xs text-muted-foreground w-12 shrink-0">{a.account_number || "—"}</span>
                    <span className="truncate">{a.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
